import { z } from 'zod'

import {
	activitySchema,
	anchoringSchema,
	minimalStepSchema,
	priceLimitsSchema,
	pricingTacticsMDSchema,
	pricingTacticsMLSchema,
	pricingTacticsPromoSchema,
	pricingTacticsRBSchemaAsync,
	productAssignmentSchema,
} from '@/entities/pricing-campaign/@x/settings-template'
import {
	scenarioBusinessSchema,
	scenarioSchema,
	scenarioStopListSchema,
	scenarioStrategySchema,
} from '@/entities/scenario/@x/settings-template'

import { SettingsTemplateType } from '@/shared/generated'

export const createSettingsTemplateSchemaAsync = z.discriminatedUnion(
	'template_type',
	[
		z
			.object({
				template_type: z.literal(SettingsTemplateType.PricingCampaign),
				name: z.string().min(1),
				body: z.object({
					settings: z.object({
						product_assignments: z.array(productAssignmentSchema),
						activity: activitySchema,
						price_limits: priceLimitsSchema,
						anchoring: anchoringSchema,
						rounding: z.array(z.any()),
						minimal_steps: z.array(minimalStepSchema),
						pricing_tactics: z.discriminatedUnion('engine', [
							pricingTacticsPromoSchema,
							pricingTacticsRBSchemaAsync,
							pricingTacticsMLSchema,
							pricingTacticsMDSchema,
						]),
					}),
				}),
			})
			.transform((data) => ({
				...data,
				body: {
					...data.body,
					name: data.name,
				},
			})),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignActivity),
			name: z.string().min(1),
			body: z.object({
				activity: activitySchema,
			}),
		}),
		z.object({
			template_type: z.literal(
				SettingsTemplateType.PricingCampaignProductAssignment
			),
			name: z.string().min(1),
			body: z.object({
				product_assignments: z.array(productAssignmentSchema),
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignAnchoring),
			name: z.string().min(1),
			body: z.object({
				anchoring: anchoringSchema,
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignRounding),
			name: z.string().min(1),
			body: z.object({
				rounding: z.array(z.any()),
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignLimits),
			name: z.string().min(1),
			body: z.object({
				price_limits: priceLimitsSchema,
			}),
		}),
		z.object({
			template_type: z.literal(
				SettingsTemplateType.PricingCampaignMinimalSteps
			),
			name: z.string().min(1),
			body: z.object({
				minimal_steps: z.array(minimalStepSchema),
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignTacticsMd),
			name: z.string().min(1),
			body: z.object({
				pricing_tactics: pricingTacticsMDSchema,
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignTacticsMl),
			name: z.string().min(1),
			body: z.object({
				pricing_tactics: pricingTacticsMLSchema,
			}),
		}),
		z.object({
			template_type: z.literal(
				SettingsTemplateType.PricingCampaignTacticsPromo
			),
			name: z.string().min(1),
			body: z.object({
				pricing_tactics: pricingTacticsPromoSchema,
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.PricingCampaignTacticsRb),
			name: z.string().min(1),
			body: z.object({
				pricing_tactics: pricingTacticsRBSchemaAsync,
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.Scenario),
			name: z.string().min(1),
			body: z.object({
				...scenarioSchema.shape,
				pricing_campaigns: z.array(
					z.object({
						name: z.string().min(1),
						order: z.number(),
						settings: z.object({
							product_assignments: z.array(productAssignmentSchema),
							activity: activitySchema,
							price_limits: priceLimitsSchema,
							anchoring: anchoringSchema,
							rounding: z.array(z.any()),
							minimal_steps: z.array(minimalStepSchema),
							pricing_tactics: z.discriminatedUnion('engine', [
								pricingTacticsPromoSchema,
								pricingTacticsRBSchemaAsync,
								pricingTacticsMLSchema,
								pricingTacticsMDSchema,
							]),
						}),
					})
				),
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.ScenarioStrategy),
			name: z.string().min(1),
			body: z.object({
				strategy: scenarioStrategySchema,
			}),
		}),
		z.object({
			template_type: z.literal(
				SettingsTemplateType.ScenarioBusinessConstraints
			),
			name: z.string().min(1),
			body: z.object({
				business: scenarioBusinessSchema,
			}),
		}),
		z.object({
			template_type: z.literal(SettingsTemplateType.ScenarioStopList),
			name: z.string().min(1),
			body: z.object({
				stop_list: scenarioStopListSchema,
			}),
		}),
	]
)
